import dayjs from "dayjs";
import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api";
import { Booking, EventType } from "../types";
import { AdminShell } from "../components/admin/AdminShell";
import { Toast } from "../components/Toast";
import { useConfirm } from "../hooks/useConfirm";
import { useToast } from "../hooks/useToast";
import { groupBookingsByDate, parseBookingNote } from "../utils/bookings";
import { addMinutes, formatCreatedAt, formatMeetingDate } from "../utils/date";
import { formatPrice } from "../utils/payment";

type MeetingTab = "upcoming" | "past" | "range";
type BookingStatusFilter = "active" | "cancelled";
const bookingsPageSize = 20;

function emailActionLabel(action?: string) {
  if (action === "rescheduled") return "Reschedule email";
  if (action === "cancelled") return "Cancel email";
  return "Booking confirmation email";
}

function paymentStatusLabel(status?: string) {
  if (status === "PAID") return "Төлөгдсөн";
  if (status === "PENDING") return "Хүлээгдэж байна";
  if (status === "EXPIRED") return "Хугацаа дууссан";
  if (status === "FAILED") return "Амжилтгүй";
  if (status === "MANUAL") return "Гараар баталгаажсан";
  return "Үүсээгүй";
}

function depositLabel(booking: Booking) {
  const amount = Number(booking.paymentAmount || 0);
  if (amount <= 0) return "Урьдчилгаа: -";
  return `Урьдчилгаа: ${formatPrice(amount)}₮ · ${paymentStatusLabel(booking.paymentStatus)}`;
}

export function AdminBookingsPage() {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [events, setEvents] = useState<EventType[]>([]);
  const [bookingCategory, setBookingCategory] = useState<"regular" | "portrait">("regular");
  const [statusFilter, setStatusFilter] = useState<BookingStatusFilter>("active");
  const [bookingFilter, setBookingFilter] = useState<MeetingTab>("upcoming");
  const [rangeStart, setRangeStart] = useState(dayjs().format("YYYY-MM-DD"));
  const [rangeEnd, setRangeEnd] = useState(dayjs().add(30, "day").format("YYYY-MM-DD"));
  const [bookingPage, setBookingPage] = useState(1);
  const [bookingTotal, setBookingTotal] = useState(0);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingsError, setBookingsError] = useState("");
  const [bookingsHasMore, setBookingsHasMore] = useState(false);
  const [reschedule, setReschedule] = useState<Booking | null>(null);
  const [rescheduleDate, setRescheduleDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [rescheduleTime, setRescheduleTime] = useState("");
  const [rescheduleNote, setRescheduleNote] = useState("");
  const [rescheduleError, setRescheduleError] = useState("");
  const [rescheduleSlotsLoading, setRescheduleSlotsLoading] = useState(false);
  const [availability, setAvailability] = useState<Record<string, string[]>>({});
  const bookingRequestId = useRef(0);
  const bookingsLoadingRef = useRef(false);
  const { toast, showToast } = useToast();
  const { askConfirm, confirmModal } = useConfirm();

  const fetchBookingsPage = useCallback(
    async (page = 1, append = false, limit = bookingsPageSize) => {
      const requestId = ++bookingRequestId.current;
      bookingsLoadingRef.current = true;
      setBookingsLoading(true);
      setBookingsError("");
      try {
        const result = await api.getBookingsPage({
          status: statusFilter === "active" ? "active" : undefined,
          category: bookingCategory,
          view: bookingFilter,
          rangeStart: bookingFilter === "range" ? rangeStart : undefined,
          rangeEnd: bookingFilter === "range" ? rangeEnd : undefined,
          page,
          limit,
        });
        if (requestId !== bookingRequestId.current) return;
        setBookings((current) => {
          return append ? [...current, ...result.items] : result.items;
        });
        setBookingPage(append ? page : Math.max(1, Math.ceil(result.items.length / bookingsPageSize)));
        setBookingTotal(result.total);
        setBookingsHasMore(append ? page * bookingsPageSize < result.total : result.items.length < result.total);
      } catch (error) {
        if (requestId === bookingRequestId.current) {
          setBookingsError(error instanceof Error ? error.message : "Bookings ачаалахад алдаа гарлаа.");
        }
      } finally {
        if (requestId === bookingRequestId.current) {
          bookingsLoadingRef.current = false;
          setBookingsLoading(false);
        }
      }
    },
    [bookingCategory, bookingFilter, rangeEnd, rangeStart, statusFilter],
  );

  const refreshBookings = useCallback(() => {
    if (bookingsLoadingRef.current) return;
    const visibleLimit = Math.max(bookingsPageSize, bookingPage * bookingsPageSize);
    fetchBookingsPage(1, false, visibleLimit).catch((error) => {
      console.error("Failed to refresh bookings", error);
    });
  }, [bookingPage, fetchBookingsPage]);

  useEffect(() => {
    api.getEvents(true).then(setEvents);
  }, []);

  useEffect(() => {
    setBookingPage(1);
    setBookings([]);
    setBookingTotal(0);
    setBookingsHasMore(false);
    fetchBookingsPage(1).catch((error) => {
      console.error("Failed to load bookings", error);
    });
  }, [bookingCategory, bookingFilter, fetchBookingsPage, rangeEnd, rangeStart, statusFilter]);

  useEffect(() => {
    const timer = window.setInterval(refreshBookings, 15000);
    const handleFocus = () => refreshBookings();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") refreshBookings();
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [refreshBookings]);

  useEffect(() => {
    if (!reschedule) return;
    let active = true;
    setRescheduleSlotsLoading(true);
    setRescheduleError("");
    api
      .getAdminAvailability(reschedule.eventId, dayjs(rescheduleDate).format("YYYY-MM"))
      .then((result) => {
        if (active) setAvailability(result);
      })
      .catch((error) => {
        if (active) {
          setAvailability({});
          setRescheduleError(error instanceof Error ? error.message : "Цагийн сонголт ачааллахад алдаа гарлаа.");
        }
      })
      .finally(() => {
        if (active) setRescheduleSlotsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [reschedule, rescheduleDate]);

  function cancelBooking(booking: Booking) {
    askConfirm({
      title: "Захиалгыг цуцлах уу?",
      message: `${booking.name} - ${booking.date} ${booking.time} захиалгыг цуцалж, Mongo дээр cancelled төлөвтэй үлдээнэ.`,
      confirmLabel: "Захиалга цуцлах",
      onConfirm: async () => {
        await api.deleteBooking(booking.id);
        const cancelledAt = new Date().toISOString();
        setBookings((current) =>
          current.map((item) =>
            item.id === booking.id
              ? { ...item, status: "cancelled", cancelledAt, updatedAt: cancelledAt, cancelledReason: item.cancelledReason || "No reason provided." }
              : item,
          ),
        );
        setStatusFilter("cancelled");
        showToast("Захиалга cancelled төлөвтэй боллоо.");
        refreshBookings();
      },
    });
  }

  async function saveReschedule(e: FormEvent) {
    e.preventDefault();
    if (!reschedule || !rescheduleTime) return;
    setRescheduleError("");
    try {
      await api.updateBooking(reschedule.id, {
        eventId: reschedule.eventId,
        date: rescheduleDate,
        time: rescheduleTime,
        rescheduleNote,
      });
      setReschedule(null);
      setRescheduleTime("");
      setRescheduleNote("");
      showToast("Захиалга reschedule хийгдлээ.");
      refreshBookings();
    } catch (error) {
      setRescheduleError(error instanceof Error ? error.message : "Reschedule хийхэд алдаа гарлаа.");
    }
  }

  function openReschedule(booking: Booking) {
    setReschedule(booking);
    setRescheduleDate(booking.date);
    setRescheduleTime(booking.time);
    setRescheduleNote(booking.rescheduleNote || "");
    setRescheduleError("");
  }

  function scheduleInviteeAgain(booking: Booking) {
    const meta = parseBookingNote(booking.note);
    const params = new URLSearchParams({
      name: booking.name,
      email: booking.email,
      phone: booking.phone,
      quantity: meta.quantity,
      gift: meta.gift,
    });

    window.location.href = `/book/${booking.eventId}?${params.toString()}`;
  }

  const rescheduleSlots = reschedule ? availability[rescheduleDate] || [] : [];
  const currentSlotVisible =
    reschedule &&
    reschedule.date === rescheduleDate &&
    bookings.find((booking) => booking.id === reschedule.id)?.eventId === reschedule.eventId &&
    !rescheduleSlots.includes(reschedule.time);
  const visibleBookings = bookings;
  const groupedBookings = groupBookingsByDate(visibleBookings, bookingFilter === "past" ? "desc" : "asc");

  return (
    <AdminShell active="meetings" title="Meetings">
      <div className="meetings-toolbar">
        <div className="room-tabs">
          <button className={bookingCategory === "regular" ? "active" : ""} type="button" onClick={() => setBookingCategory("regular")}>
            Regular
          </button>
          <button className={bookingCategory === "portrait" ? "active" : ""} type="button" onClick={() => setBookingCategory("portrait")}>
            Portrait
          </button>
        </div>
        <small>Displaying {visibleBookings.length} of {bookingTotal} Events</small>
      </div>

      <section className="meetings-card">
        <div className="meetings-tabs">
          <button className={bookingFilter === "upcoming" ? "active" : ""} type="button" onClick={() => setBookingFilter("upcoming")}>Upcoming</button>
          <button className={bookingFilter === "past" ? "active" : ""} type="button" onClick={() => setBookingFilter("past")}>Past</button>
          <button className={bookingFilter === "range" ? "active" : ""} type="button" onClick={() => setBookingFilter("range")}>Date Range⌄</button>
          <div className="status-tabs" aria-label="Booking status">
            <button className={statusFilter === "active" ? "active" : ""} type="button" onClick={() => setStatusFilter("active")}>Active</button>
            <button className={statusFilter === "cancelled" ? "active" : ""} type="button" onClick={() => setStatusFilter("cancelled")}>Canceled</button>
          </div>
          {bookingFilter === "range" && (
            <div className="date-range-controls">
              <input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} />
              <span>-</span>
              <input type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} />
            </div>
          )}
        </div>

        {visibleBookings.length === 0 && !bookingsLoading && <p className="empty-meetings">Одоогоор захиалга алга.</p>}
        {bookingsError && <p className="booking-error">{bookingsError}</p>}
        {groupedBookings.map((group) => (
          <section className="meeting-day" key={group.date}>
            <h2>{formatMeetingDate(group.date)}</h2>
            {group.items.map((booking) => {
              const event = events.find((item) => item.id === booking.eventId);
              const meta = parseBookingNote(booking.note);
              const isCancelled = (booking.status || "active") === "cancelled";
              const emailLogs = booking.emailLogs || [];
              const hasConfirmationEmail = emailLogs.some((email) => email.action === "created");
              const showPendingPaymentNotice = booking.paymentStatus === "PENDING" && !hasConfirmationEmail;
              const showPaidEmailPendingNotice = booking.paymentStatus === "PAID" && !hasConfirmationEmail;
              const showGenericNoEmailNotice = emailLogs.length === 0 && !showPendingPaymentNotice && !showPaidEmailPendingNotice;
              const rescheduleHistory =
                booking.rescheduleHistory?.length
                  ? booking.rescheduleHistory
                  : booking.updatedAt && booking.rescheduledFrom
                    ? [
                        {
                          changedAt: booking.updatedAt,
                          note: booking.rescheduleNote || "",
                          from: booking.rescheduledFrom,
                          to: { eventTitle: booking.eventTitle, date: booking.date, time: booking.time },
                        },
                      ]
                    : [];
              const hasReschedule = rescheduleHistory.length > 0;
              return (
                <details className={`meeting-item${isCancelled ? " cancelled" : ""}`} key={booking.id}>
                  <summary className="meeting-row">
                    <span className="meeting-dot" style={{ background: event?.color || "#20d6df" }} />
                    <span className="meeting-time">
                      {booking.time} – {addMinutes(booking.time, event?.durationMinutes || 30)}
                    </span>
                    <div className="meeting-person">
                      <strong>{booking.name}</strong>
                      <span>Event type <b>{booking.eventTitle}</b></span>
                      {hasReschedule && !isCancelled && (
                        <small className="reschedule-mark" title={booking.rescheduleNote || undefined}>
                          Rescheduled {formatCreatedAt(rescheduleHistory[0].changedAt)}
                        </small>
                      )}
                      {isCancelled && (
                        <small className="cancelled-note">
                          Canceled by PicShot Self Portrait Studio: {booking.cancelledReason || "No reason provided."}
                        </small>
                      )}
                    </div>

                    <span className="meeting-payment">{depositLabel(booking)}</span>
                    <span className="meeting-details-toggle">Details</span>
                  </summary>

                  <div className="meeting-expanded">
                    <aside className="meeting-actions">
                      {!isCancelled && (
                        <>
                          <button type="button" onClick={() => openReschedule(booking)}>↻ Reschedule</button>
                          <button className="danger-button" type="button" onClick={() => cancelBooking(booking)}>🗑 Цуцлах</button>
                        </>
                      )}
                      {isCancelled && (
                        <button type="button" onClick={() => scheduleInviteeAgain(booking)}>↻ Schedule Invitee Again</button>
                      )}
                    </aside>

                    <div className="meeting-detail-body">
                      <section>
                        <h3>INVITEE</h3>
                        <p><b>{booking.name}</b></p>
                        <p>{booking.email}</p>
                        <p>{booking.phone}</p>
                      </section>
                      <section>
                        <h3>LOCATION</h3>
                        <p>Баянгол дүүрэг модны 2 ын дунд зам дагуу World mongolia tower 3 давхарт 308 тоот</p>
                      </section>
                      <section>
                        <h3>INVITEE TIME ZONE</h3>
                        <p>Ulaanbaatar Time</p>
                      </section>
                      <section>
                        <h3>QUESTIONS</h3>
                        <p>Утасны дугаар ?</p>
                        <p>{booking.phone}</p>
                        <p>Зураг даруулах хүний тоо</p>
                        <p>{meta.quantity || "-"}</p>
                        {meta.childGender && (
                          <>
                            <p>Хүү / Охин</p>
                            <p>{meta.childGender}</p>
                          </>
                        )}
                        {meta.gift && (
                          <>
                            <p>Gift card code ? байхгүй бол хоосон орхино уу</p>
                            <p>{meta.gift}</p>
                          </>
                        )}
                      </section>
                      <section>
                        <h3>MEETING HOST</h3>
                        <p>Host will attend this meeting</p>
                      </section>
                      <p className="meeting-created">Created {formatCreatedAt(booking.createdAt)} by PicShot Self Portrait Studio</p>
                      {hasReschedule && !isCancelled && (
                        <p className="reschedule-mark">
                          Reschedule хийгдсэн: {formatCreatedAt(rescheduleHistory[0].changedAt)}
                          {booking.rescheduledFrom?.eventTitle && ` • Өмнөх багц: ${booking.rescheduledFrom.eventTitle}`}
                        </p>
                      )}
                      {hasReschedule && booking.rescheduleNote && (
                        <section className="reschedule-note-section">
                          <h3>RESCHEDULE ТАЙЛБАР</h3>
                          <p>{booking.rescheduleNote}</p>
                        </section>
                      )}
                      {isCancelled && (
                        <p className="cancelled-note">
                          Canceled by PicShot Self Portrait Studio: {booking.cancelledReason || "No reason provided."}
                        </p>
                      )}

                      <section className="meeting-timeline">
                        <h3>TIMELINE</h3>
                        <div className="timeline-item">
                          <span />
                          <p><b>New event booked:</b> {booking.name} booked a {booking.eventTitle} on {formatCreatedAt(booking.createdAt)}</p>
                        </div>
                        {showPendingPaymentNotice && (
                          <div className="timeline-item muted">
                            <span />
                            <div>
                              <p><b>Төлбөр хүлээгдэж байна:</b> Баталгаажсан mail хараахан яваагүй.</p>
                              <small>QPay төлөгдөхөд автоматаар “Таны цаг баталгаажлаа” mail илгээгдэнэ.</small>
                            </div>
                          </div>
                        )}
                        {showPaidEmailPendingNotice && (
                          <div className="timeline-item muted">
                            <span />
                            <div>
                              <p><b>Төлбөр төлөгдсөн:</b> Баталгаажсан mail илгээхээр хүлээгдэж байна.</p>
                              <small>Дараагийн payment check/callback дээр mail log энд нэмэгдэнэ.</small>
                            </div>
                          </div>
                        )}
                        {showGenericNoEmailNotice && (
                          <div className="timeline-item muted">
                            <span />
                            <p>Email log байхгүй. Дараагийн шинэ/reschedule/cancel email-үүд энд хадгалагдана.</p>
                          </div>
                        )}
                        {emailLogs.map((email, index) => (
                          <div className="timeline-item" key={`${email.subject}-${email.sentAt || email.savedAt || index}`}>
                            <span />
                            <div>
                              <p><b>{emailActionLabel(email.action)}:</b> {email.mode === "sent" ? "Sent email to invitee" : "Saved to Mongo outbox"}</p>
                              <small>{email.to} • {formatCreatedAt(email.sentAt || email.savedAt || "")}</small>
                            </div>
                          </div>
                        ))}
                        {rescheduleHistory.map((item, index) => (
                          <div className="timeline-item" key={`${item.changedAt}-${index}`}>
                            <span />
                            <div>
                              <p>
                                <b>Rescheduled:</b> {item.from.date} {item.from.time} → {item.to.date} {item.to.time}
                              </p>
                              <small>
                                {formatCreatedAt(item.changedAt)}
                                {item.from.eventTitle !== item.to.eventTitle && ` • ${item.from.eventTitle} → ${item.to.eventTitle}`}
                                {item.note && ` • ${item.note}`}
                              </small>
                            </div>
                          </div>
                        ))}
                      </section>
                    </div>
                  </div>
                </details>
              );
            })}
          </section>
        ))}
        {bookingsLoading && <p className="end-list">Loading...</p>}
        {bookingsHasMore && !bookingsLoading && (
          <div className="load-more-row">
            <button type="button" onClick={() => fetchBookingsPage(bookingPage + 1, true)}>
              Load more
            </button>
          </div>
        )}
        {visibleBookings.length > 0 && !bookingsHasMore && !bookingsLoading && <p className="end-list">You've reached the end of the list</p>}
      </section>

      {reschedule && (
        <div className="modal-backdrop" role="presentation">
          <form className="event-modal" onSubmit={saveReschedule}>
            <div className="modal-title">
              <h2>Reschedule</h2>
              <button type="button" onClick={() => setReschedule(null)}>×</button>
            </div>
            <p className="muted">{reschedule.name} • {reschedule.eventTitle}</p>
            <select
              value={reschedule.eventId}
              onChange={(e) => {
                const event = events.find((item) => item.id === e.target.value);
                if (!event) return;
                setReschedule({ ...reschedule, eventId: event.id, eventTitle: event.title });
                setRescheduleTime("");
                setRescheduleError("");
              }}
            >
              {events.map((event) => (
                <option key={event.id} value={event.id}>{event.title}</option>
              ))}
            </select>
            <input
              type="date"
              value={rescheduleDate}
              onChange={(e) => {
                setRescheduleDate(e.target.value);
                setRescheduleTime("");
                setRescheduleError("");
              }}
            />
            <select
              required
              disabled={rescheduleSlotsLoading}
              value={rescheduleTime}
              onChange={(e) => setRescheduleTime(e.target.value)}
            >
              <option value="">{rescheduleSlotsLoading ? "Цаг уншиж байна..." : "Цаг сонгох"}</option>
              {currentSlotVisible && <option value={reschedule.time}>{reschedule.time} (одоогийн)</option>}
              {rescheduleSlots.map((slot) => (
                <option key={slot} value={slot}>{slot}</option>
              ))}
            </select>
            {!rescheduleSlotsLoading && !rescheduleError && rescheduleSlots.length === 0 && !currentSlotVisible && (
              <div className="booking-error">Сонгосон өдөр сул цаг алга.</div>
            )}
            <label className="modal-field">
              <span>Нэмэлт тайлбар</span>
              <textarea
                placeholder="Жишээ: багц сольсон, үйлчлүүлэгчийн хүсэлтээр өдөр/цаг өөрчилсөн гэх мэт"
                value={rescheduleNote}
                onChange={(e) => setRescheduleNote(e.target.value)}
              />
            </label>
            {rescheduleError && <div className="booking-error">{rescheduleError}</div>}
            <button className="primary-action" type="submit">Хадгалах</button>
          </form>
        </div>
      )}
      {confirmModal}
      <Toast message={toast} />
    </AdminShell>
  );
}
